import {DigitalAssetASchema} from '@cohort/admin-schemas/digitalAsset';
import {MerchantASchema} from '@cohort/admin-schemas/merchant';
import {PerkASchema} from '@cohort/admin-schemas/perk';
import {
  asArray,
  DateSchema,
  EmailSchema,
  LocalizedRichTextSchema,
  LocalizedStringSchema,
  PositiveIntegerSchema,
} from '@cohort/shared/schema/common';
import {NetworkSchema} from '@cohort/shared/schema/common/blockchain';
import {CampaignTypeSchema} from '@cohort/shared/schema/common/campaign';
import {DigitalAssetCollectionStatusSchema} from '@cohort/shared/schema/common/digitalAssetCollection';
import {OwnershipObtentionTypeSchema} from '@cohort/shared/schema/common/ownerships';
import {PaginationParamsSchema} from '@cohort/shared/schema/common/pagination';
import {z} from 'zod';

export const SmartContractASchema = z.object({
  network: NetworkSchema,
  address: z.string(),
  blockExplorerUrl: z.string().url(),
  openseaUrl: z.string().url(),
});
export type SmartContractADto = z.infer<typeof SmartContractASchema>;

export const DigitalAssetCollectionASchema = z.object({
  id: z.string().uuid(),
  title: LocalizedStringSchema,
  merchant: MerchantASchema,
  status: DigitalAssetCollectionStatusSchema,
  imageFileKey: z.string(),
  animationFileKey: z.string().nullable(),
  description: LocalizedRichTextSchema,
  internalName: z.string().min(3),
  createdAt: DateSchema,
  updatedAt: DateSchema,
  perks: z.array(PerkASchema),
  maxOwnershipsPerUser: PositiveIntegerSchema.nullable(),
  nftEnabled: z.boolean(),
  smartContract: SmartContractASchema.nullable(),
});
export type DigitalAssetCollectionADto = z.infer<typeof DigitalAssetCollectionASchema>;

export const CreateDigitalAssetCollectionASchema = z.object({
  description: LocalizedRichTextSchema,
  title: LocalizedStringSchema,
  internalName: z.string().min(3),
  imageFileKey: z.string(),
  animationFileKey: z.string().nullable().optional(),
  perkIds: z.array(z.string().uuid()).optional(),
  maxOwnershipsPerUser: PositiveIntegerSchema.nullable().optional(),
  nftEnabled: z.boolean(),
});

export type CreateDigitalAssetCollectionADto = z.infer<typeof CreateDigitalAssetCollectionASchema>;

export const PatchDigitalAssetCollectionRequestASchema = z.object({
  description: LocalizedRichTextSchema.optional(),
  title: LocalizedStringSchema.optional(),
  internalName: z.string().min(3).optional(),
  imageFileKey: z.string().optional(),
  animationFileKey: z.string().nullable().optional(),
  perkIds: z.array(z.string().uuid()).optional(),
  maxOwnershipsPerUser: PositiveIntegerSchema.nullable().optional(),
  nftEnabled: z.literal(true).optional(),
});

export type PatchDigitalAssetCollectionRequestADto = z.infer<
  typeof PatchDigitalAssetCollectionRequestASchema
>;

export const DuplicateDigitalAssetCollectionASchema = z.object({
  internalName: z.string().min(3),
  nftEnabled: z.boolean(),
});

export type DuplicateDigitalAssetCollectionADto = z.infer<
  typeof DuplicateDigitalAssetCollectionASchema
>;

export const GetDigitalAssetCollectionsSortParamASchema = z.enum(['updatedAt', 'internalName']);

export type GetDigitalAssetCollectionsSortParamADto = z.infer<
  typeof GetDigitalAssetCollectionsSortParamASchema
>;

export const GetDigitalAssetCollectionsParamsASchema = PaginationParamsSchema.extend({
  status: z.preprocess(asArray, z.array(DigitalAssetCollectionStatusSchema).optional()),
  orderBy: GetDigitalAssetCollectionsSortParamASchema.optional(),
  perkId: z.string().uuid().optional(),
  digitalAssetCollectionIds: z.preprocess(asArray, z.array(z.string().uuid()).optional()),
});

export type GetDigitalAssetCollectionsParamsADto = z.infer<
  typeof GetDigitalAssetCollectionsParamsASchema
>;

export const GetCollectionOwnersViewParamsASchema = PaginationParamsSchema;
export type GetCollectionOwnersViewParamsADto = z.infer<
  typeof GetCollectionOwnersViewParamsASchema
>;

export const CollectionsAnalyticsViewASchema = DigitalAssetCollectionASchema.extend({
  campaignsCount: z.number(),
  ownershipsCount: z.number(),
});

export type CollectionsAnalyticsViewADto = z.infer<typeof CollectionsAnalyticsViewASchema>;

export const GetCollectionsAnalyticsViewParamsASchema =
  GetDigitalAssetCollectionsParamsASchema.extend({
    digitalAssetCollectionId: z.string().uuid().optional(),
  });

export type GetCollectionsAnalyticsViewParamsADto = z.infer<
  typeof GetCollectionsAnalyticsViewParamsASchema
>;

export const CollectionOwnersViewASchema = z.object({
  digitalAsset: DigitalAssetASchema,
  campaign: z
    .object({
      id: z.string().uuid(),
      internalName: z.string(),
      type: CampaignTypeSchema,
    })
    .nullable(),
  user: z
    .object({
      id: z.string().uuid(),
      email: EmailSchema,
    })
    .nullable(),
  wallet: z
    .object({
      id: z.string().uuid(),
      address: z.string(),
    })
    .nullable(),
  obtentionType: OwnershipObtentionTypeSchema,
  createdAt: DateSchema.nullable(),
});

export type CollectionOwnersViewADto = z.infer<typeof CollectionOwnersViewASchema>;
